import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import {
  NoPermissions,
  PermissionKeys,
  PermissionLevel,
  PermissionType,
} from 'constants/userConstants';

const swipeclockUserSlice = createSlice({
  name: 'swipeclockUser',
  initialState: {
    dependencyKey: undefined,
    directReports: [],
    employeeIds: undefined,
    features: [],
    permissions: [],
    permissionsDictionary: {},
    productLevel: undefined,
    roles: [],
    metrics: {
      user: {},
      site: {},
    },
  },
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
    setDependencyKey: (state, action) => {
      state.dependencyKey = action.payload;
    },
    setMasterData: (state, action) => {
      const {
        directReports,
        employeeIds,
        features,
        permissions,
        roles,
        productLevel,
      } = action.payload ?? {};
      state.directReports = directReports;
      state.employeeIds = employeeIds;
      state.features = features;
      state.permissions = permissions;
      state.roles = roles;
      state.productLevel = productLevel;

      state.permissionsDictionary = createPermissionsDictionary(permissions);
    },
    setMetricsData: (state, action) => {
      state.metrics = action.payload;
    },
  },
});

export const { setDependencyKey, setMasterData, setMetricsData } =
  swipeclockUserSlice.actions;

export default swipeclockUserSlice.reducer;

const selectSwipeclockUserState = (state) => state.swipeclockUser;

// Use createSelector to create a memoized selector for the dependencyKey
export const selectDependencyKey = createSelector(
  selectSwipeclockUserState,
  (scUser) => scUser.dependencyKey
);

export const selectFeatures = createSelector(
  selectSwipeclockUserState,
  (scUser) => scUser.features
);

/**
 * Create a permissions dictionary for the user
 * To Check permissions we are returning masterData with modified permissions
 * instead of giving us string values, we now can check permissions based off
 * of a bool value.
 *
 * IE:
 *     const { permissionsDictionary } = user.masterData
 *     if (permissions.modifySchedule.all) {
 *        // Do something awesome
 *     }
 */
function createPermissionsDictionary(permissions) {
  // Note: Features are site wide meaning the site supports the feature
  //       Permissions are for the current user
  //       For each item we need to check if site supports it and
  //       then need to verify the user has permission
  const modifiedPermissions = Object.keys(PermissionType).reduce((obj, key) => {
    return { ...obj, ...modifyPermissionLevel(key, permissions) };
  }, {});

  return modifiedPermissions;
}

function modifyPermissionLevel(type, permissions) {
  const permissionMatch = (permissions || []).find(
    (x) => x.permissionType === PermissionType[type]
  );

  if (permissionMatch) {
    const value = PermissionKeys.reduce((obj, key) => {
      const lvl = permissionMatch.permissionLevel === PermissionLevel[key];
      return { ...obj, [key]: lvl };
    }, {});

    return { [type]: value };
  }

  return {
    [type]: NoPermissions,
  };
}
